import { type ClassValue, clsx } from 'clsx';
import dayjs from 'dayjs';
import { SignJWT } from 'jose';
import { twMerge } from 'tailwind-merge';
import config from './config/config';

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean;
  }
}
export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function currecyFormatter(number: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(number);
}

export function generateStaticUrl(url: string) {
  return `${window.location.origin}/../${url}`;
}
export async function makeFileFromUrl(url: string) {
  if (!url) return null;
  const staticUrl = generateStaticUrl(url);
  const response = await fetch(staticUrl);
  if (!response.ok) {
    return null;
  }
  const blob = await response.blob();
  const filename = staticUrl.split('/').pop() || 'document';
  return new File([blob], filename, { type: blob.type });
}

export const makeCsv = async (rows: any, filename: any) => {
  const separator = ';';
  const keys = Object.keys(rows[0]);

  const csvContent = `${keys.join(separator)}\n${rows
    .map((row: any) =>
      keys
        .map((k) => {
          let cell = row[k] === null || row[k] === undefined ? '' : row[k];

          cell =
            cell instanceof Date
              ? cell.toLocaleString()
              : cell.toString().replace(/"/g, '""');

          if (cell.search(/("|,|\n)/g) >= 0) {
            cell = `"${cell}"`;
          }
          return cell;
        })
        .join(separator)
    )
    .join('\n')}`;

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  if (navigator.msSaveBlob) {
    // In case of IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    const link = document.createElement('a');
    if (link.download !== undefined) {
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

export const formatInputForSQL = (str: any) => {
  return str
    .replace(/"/g, '') // 1. remove " with ''
    .replace(/[\t\n]/g, ' '); // 2. remove \t with a " "
};

export const isInputInvalid = (inputs: { key: string; value: any }[]) => {
  return inputs.filter((input) => {
    return input.value === undefined && input.value === null;
  });
};

export const escapeAnsiColor = (str: string) => {
  return str.replace(
    /[\u001b\u009b][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g,
    ''
  );
};

export function buildUpdatedUrl(
  pathname: string,
  currentSearchParams: string,
  updates: Record<string, string>
): string {
  const params = new URLSearchParams(currentSearchParams);
  Object.entries(updates).forEach(([key, value]) => {
    params.set(key, value);
  });
  return `${pathname}?${params.toString()}`;
}

export const mapDataToSuberbill = (data: any) => ({
  patient_information: {
    franchise_name: data?.franchise_name,
    location_id: data?.location_id,
    tax_id: data?.tax_id,
    npi: data?.npi,
    employee_name: data?.employee_name,
    store_phone_number: data?.store_phone_number,
    patient_id: data?.patient_id,
    patient_name: data?.patient_name,
    patient_dob: dayjs(data?.patient_dob).isValid()
      ? dayjs(data?.patient_dob)
      : null,
    eligiblity: data?.eligiblity,
    sales_invoiceNo: data?.sales_invoiceNo,
    invoice_creationDate: dayjs(data?.invoice_creationDate).isValid()
      ? dayjs(data?.invoice_creationDate)
      : null,
    date_of_delivery: dayjs(data?.date_of_delivery).isValid()
      ? dayjs(data?.date_of_delivery)
      : null,
    sales_locationName: data?.sales_locationName,
    date_of_service: dayjs(data?.date_of_service).isValid()
      ? dayjs(data?.date_of_service)
      : null,
  },
  insurance_details: {
    primary_insName: data?.primary_insName,
    primary_insNumber: data?.primary_insNumber,
    primary_groupName: data?.primary_groupName,
    primary_groupNo: data?.primary_groupNo,
    primary_policy: data?.primary_policy,
    primary_subscriberType: data?.primary_subscriberType,
    primary_active_insPolicy: data?.primary_active_insPolicy,
    ins_effectiveDate: dayjs(data?.ins_effectiveDate).isValid()
      ? dayjs(data?.ins_effectiveDate)
      : null,
    primary_subscriberName: data?.primary_subscriberName,
    primary_subscriberDOB: dayjs(data?.primary_subscriberDOB).isValid()
      ? dayjs(data?.primary_subscriberDOB)
      : null,
    secondary_insName: data?.secondary_insName,
    secondary_insNumber: data?.secondary_insNumber,
    secondary_groupName: data?.secondary_groupName,
    secondary_groupNo: data?.secondary_groupNo,
    secondary_policy: data?.secondary_policy,
    secondary_subscriberType: data?.secondary_subscriberType,
    secondary_activeInspolicy: data?.secondary_activeInspolicy,
    secondary_ins_effectiveDate: dayjs(
      data?.secondary_ins_effectiveDate
    ).isValid()
      ? dayjs(data?.secondary_ins_effectiveDate)
      : null,
    secondary_subscriberName: data?.secondary_subscriberName,
    secondary_subscriberDOB: dayjs(data?.secondary_subscriberDOB).isValid()
      ? dayjs(data?.secondary_subscriberDOB)
      : null,
  },
  amount_details: {
    age_restriction: data?.age_restriction,
    deductible_applied: data?.deductible_applied,
    patient_deductible_amt: data?.patient_deductible_amt,
    amount_met: data?.amount_met,
    remaining_amount: data?.remaining_amount,
    coInsurance: data?.coInsurance,
    flat_amount: data?.flat_amount,
    netSales: data?.netSales,
    percentage_costCovered: data?.percentage_costCovered,
    amount_paid: data?.amount_paid,
    secondary_age_restriction: data?.secondary_age_restriction,
    secondary_deductible_applied: data?.secondary_deductible_applied,
    secondary_patient_deductible_amt: data?.secondary_patient_deductible_amt,
    secondary_amount_met: data?.secondary_amount_met,
    secondary_amount_remaining: data?.secondary_amount_remaining,
    secondary_coInsurance: data?.secondary_coInsurance,
    secondary_flat_amount: data?.secondary_flat_amount,
    secondary_netSales: data?.secondary_netSales,
    secondary_percentage_costCovered: data?.secondary_percentage_costCovered,
    secondary_amount_paid: data?.secondary_amount_paid,
    billed_allowed_type: data?.billed_allowed_type,
    network_exception: data?.network_exception,
    needed_exception: data?.needed_exception,
    date_of_service: dayjs(data?.date_of_service).isValid()
      ? dayjs(data?.date_of_service)
      : null,
    billed_allowed_amt: data?.billed_allowed_amt,
    ins_rep_name: data?.ins_rep_name,
    ins_reference_number: data?.ins_reference_number,
    secondary_rep_name: data?.secondary_rep_name,
    secondary_reference_number: data?.secondary_reference_number,
    secondary_billed_allowed_type: data?.secondary_billed_allowed_type,
    secondary_billed_allowed_amt: data?.secondary_billed_allowed_amt,
    age_restriction_details: data?.age_restriction_details,
    secondary_age_restriction_details: data?.secondary_age_restriction_details,
  },
  document_upload: {
    last_purchaseDate: dayjs(data?.last_purchaseDate).isValid()
      ? dayjs(data?.last_purchaseDate)
      : null,
    benefit_frequency: data?.benefit_frequency,
    benefit_includes: data?.benefit_includes
      .split(',')
      ?.map((item: string) => ({
        label: item,
        value: item,
      })),
    prior_auth: data?.prior_auth,
    prior_auth_file: data?.prior_auth_file?.split(',').map((item: string) => ({
      name: item,
    })),
    rx_required: data?.rx_required,
    rx_required_file: data?.rx_required_file
      ?.split(',')
      .map((item: string) => ({
        name: item,
      })),
    upload_copy: data?.upload_copy?.split(',').map((item: string) => ({
      name: item,
    })),
    other_uploads: data?.other_uploads?.split(',').map((item: string) => ({
      name: item,
    })),
    notes: data?.notes,
  },
});

export const mapDataToWorkersComp = (data: any) => ({
  company_insurance_details: {
    franchise_name: data?.franchise_name,
    location_id: data?.location_id,
    tax_id: data?.tax_id,
    npi: data?.npi,
    employee_name: data?.employee_name,
    primary_insName: data?.primary_insName,
    primary_insNumber: data?.primary_insNumber,
    primary_policy: data?.primary_policy,
  },
  patient_service_details: {
    patient_id: data?.patient_id,
    patient_name: data?.patient_name,
    patient_dob: dayjs(data?.patient_dob).isValid()
      ? dayjs(data?.patient_dob)
      : null,
    invoice_no: data?.invoice_no,
    invoice_creationDate: dayjs(data?.invoice_creationDate).isValid()
      ? dayjs(data?.invoice_creationDate)
      : null,
    netSales: data?.netSales,
    uploaded_complete_workers_comp: data?.uploaded_complete_workers_comp,
    service_sales: data?.service_sales,
    service_sales_packing_slip: data?.service_sales_packing_slip
      ?.split(',')
      ?.map((item: string) => ({
        name: item,
      })),
    routine_care_form: data?.routine_care_form
      ?.split(',')
      .map((item: string) => ({
        name: item,
      })),
    authorization_loa_pdf: data?.authorization_loa_pdf
      ?.split(',')
      .map((item: string) => ({
        name: item,
      })),
    workers_comp_information_form: data?.workers_comp_information_form
      ?.split(',')
      .map((item: string) => ({
        name: item,
      })),
    hearing_aids: data?.hearing_aids,
    routine_care_checked: data?.routine_care_checked,
    date_of_service: dayjs(data?.date_of_service).isValid()
      ? dayjs(data?.date_of_service)
      : null,
  },
  bill_charge_and_mail_details: {
    date_of_service: dayjs(data?.date_of_service).isValid()
      ? dayjs(data?.date_of_service)
      : null,
    fax_emailAddress: data?.fax_emailAddress,
    notes: data?.notes,
  },
});

export const generateToken = async (
  data: Record<string, any>
): Promise<string> => {
  if (!config.jwt.secret) {
    throw new Error('JWT secret is not defined');
  }
  if (!config.jwt.expiresIn) {
    throw new Error('JWT expiresIn is not defined');
  }

  const secret = new TextEncoder().encode(config.jwt.secret);
  return new SignJWT(data)
    .setProtectedHeader({ alg: 'HS256' })
    .setExpirationTime(config.jwt.expiresIn)
    .sign(secret);
};

export const generateExpiryToken = () => {
  return new Date().getTime() + 8 * 60 * 60 * 1000;
};
